import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import siteUrls from "@/content/siteUrls.json";

export const FadeUp = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.55, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

export const TYPE_COLORS = {
  Podcast: "#7EBC37",
  Report: "#83BDE4",
  Article: "#FFA100",
  News: "#004778",
  Resource: "#C9DA2D",
};

export const TypeBadge = ({ type }) => (
  <span className="flex w-fit items-center gap-2 rounded-full border border-[#004778] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[#004778]">
    <span
      className="type-dot"
      style={{ background: TYPE_COLORS[type] || "#004778" }}
      aria-hidden="true"
    />
    {type}
  </span>
);

const SectionLabel = ({ children }) => (
  <p className="mb-5 flex items-center text-xs font-bold uppercase tracking-[0.18em] text-[#004778]">
    <span className="mr-2.5 inline-block h-2.5 w-2.5 bg-[#FFA100]" aria-hidden="true" />
    {children}
  </p>
);

export const HowHarc = () => (
  <section id="impact" className="mx-auto max-w-7xl px-6 py-28 md:py-36" data-testid="section-how-harc">
    <div className="grid gap-12 md:grid-cols-12">
      <FadeUp className="md:col-span-5">
        <SectionLabel>How HARC Creates Impact</SectionLabel>
        <h2 className="harc-heading text-4xl font-normal leading-tight tracking-tight text-[#004778] md:text-5xl">
          From complexity to lasting impact
        </h2>
      </FadeUp>
      <FadeUp delay={0.12} className="md:col-span-6 md:col-start-7">
        <div className="space-y-5 text-base leading-relaxed text-[#5C6B72] md:text-lg md:font-light">
          <p>
            Organizations come to HARC when they're facing complex environmental, infrastructure,
            and community challenges that don't have simple solutions.
          </p>
          <p>
            HARC combines science, strategic thinking, and collaboration to help organizations
            navigate complexity, make informed decisions, and move from ideas to action.
          </p>
          <p>
            By bringing together the right expertise, partners, and resources, we develop practical
            solutions that create lasting impact and help our clients solve today's challenges while
            building a stronger, more resilient future.
          </p>
        </div>
        <a href="#approach" className="mockup-link-arrow mt-8" data-testid="cta-how-we-work">
          How We Work <ArrowRight size={15} aria-hidden="true" />
        </a>
      </FadeUp>
    </div>
  </section>
);

const STORIES = [
  {
    title: "Strengthening the Alamo Area's Climate Plan",
    img: "https://harcresearch.org/wp-content/uploads/2026/03/shutterstock_2618287935.jpg",
    alt: "Alamo Area regional climate planning imagery",
    large: true,
    internal: true,
  },
  {
    title: "Community Resilience Through Urban Forestry",
    href: "https://harcresearch.org/research/community-resilience-through-urban-forestry/",
    img: "https://images.unsplash.com/photo-1582719299076-dd3f6f970dd4",
    alt: "Researcher working with field equipment",
  },
  {
    title: "DOE Southcentral Onsite Energy Technical Assistance",
    href: "https://harcresearch.org/research/doe-southcentral-onsite-energy-technical-assistance/",
    img: "https://images.unsplash.com/photo-1675130277336-23cb686f01c0",
    alt: "Wind turbines in a green field",
  },
];

const StoryCardInner = ({ s }) => (
  <>
    <img src={s.img} alt={s.alt} loading="lazy" />
    <div className="scrim" aria-hidden="true" />
    <div className="absolute inset-x-0 bottom-0 p-7">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#C9DA2D]">
        Impact Story
      </p>
      <h3
        className={`harc-heading font-normal leading-snug text-white ${
          s.large ? "text-2xl md:text-3xl" : "text-xl"
        }`}
      >
        {s.title}
      </h3>
      <p className="mt-3 text-xs italic text-white/70">
        [HARC CONTENT REQUIRED: verified outcome summary]
      </p>
      <span className="mockup-link-arrow mt-4 !text-white">
        Read the Story <ArrowRight size={14} aria-hidden="true" />
      </span>
    </div>
  </>
);

const StoryCard = ({ s, i }) => (
  <FadeUp delay={i * 0.1} className={s.large ? "md:col-span-7 md:row-span-2" : "md:col-span-5"}>
    {s.internal ? (
      <Link
        to="/impact-story"
        className={`story-card group ${s.large ? "h-[420px] md:h-full md:min-h-[560px]" : "h-[280px]"}`}
        data-testid={`story-card-${i + 1}`}
      >
        <StoryCardInner s={s} />
      </Link>
    ) : (
      <a
        href={s.href}
        target="_blank"
        rel="noopener noreferrer"
        className={`story-card group ${s.large ? "h-[420px] md:h-full md:min-h-[560px]" : "h-[280px]"}`}
        data-testid={`story-card-${i + 1}`}
      >
        <StoryCardInner s={s} />
      </a>
    )}
  </FadeUp>
);

export const ImpactStories = () => (
  <section id="stories" className="bg-[#EEF3F5] py-28 md:py-36" data-testid="section-stories">
    <div className="mx-auto max-w-7xl px-6">
      <FadeUp>
        <SectionLabel>What HARC Makes Possible</SectionLabel>
        <h2 className="harc-heading max-w-2xl text-4xl font-normal leading-tight tracking-tight text-[#004778] md:text-5xl">
          Problems solved, proven in the field
        </h2>
        <p className="mt-5 max-w-xl text-base leading-relaxed text-[#5C6B72]">
          Stories organized around the challenges our partners faced, and what changed. Sample
          stories shown are real HARC projects; final verified Impact Stories to come.
        </p>
      </FadeUp>
      <div className="mt-14 grid gap-5 md:grid-cols-12">
        {STORIES.map((s, i) => (
          <StoryCard key={s.title} s={s} i={i} />
        ))}
      </div>
    </div>
  </section>
);

export const Approach = () => (
  <section id="approach" className="mx-auto max-w-7xl px-6 py-28 md:py-36" data-testid="section-approach">
    <div className="grid items-center gap-14 md:grid-cols-12">
      <FadeUp className="md:col-span-6">
        <SectionLabel>See How We Work</SectionLabel>
        <h2 className="harc-heading text-4xl font-normal leading-tight tracking-tight text-[#004778] md:text-5xl">
          Understand. Connect. Solve. Impact.
        </h2>
        <div className="mt-7 space-y-5 text-base leading-relaxed text-[#5C6B72]">
          <p>
            Complex challenges require more than a single solution or area of expertise. HARC brings
            together science, strategy, people, and resources to see the bigger picture and create
            practical paths forward.
          </p>
          <p>
            From understanding the challenge to connecting the right expertise and turning insight
            into action, discover how HARC works to create solutions that strengthen infrastructure,
            support sustainable growth, protect our environment, and improve quality of life.
          </p>
        </div>
        <Link to="/start-a-conversation" className="mockup-link-arrow mt-8" data-testid="cta-explore-approach">
          Explore Our Approach <ArrowRight size={15} aria-hidden="true" />
        </Link>
      </FadeUp>
      <FadeUp delay={0.15} className="md:col-span-5 md:col-start-8">
        <img
          src="https://images.pexels.com/photos/6616479/pexels-photo-6616479.jpeg"
          alt="Aerial view of an intersection over a river surrounded by greenery"
          className="-mt-0 aspect-[4/5] w-full border border-[#004778]/25 object-cover md:-mt-12"
          loading="lazy"
        />
      </FadeUp>
    </div>
  </section>
);

const INSIGHT_SLUGS = [
  {
    slug: "energy-crossroads-a-harc-podcast-series",
    type: "Podcast",
    title: "Energy Crossroads: A HARC Podcast Series",
  },
  {
    slug: "harc-releases-new-report-on-building-a-critical-energy-and-water-workforce-in-harris-county",
    type: "Report",
    title: "HARC Releases New Report on Building a Critical Energy and Water Workforce in Harris County",
  },
  {
    slug: "harcs-study-on-floating-solar-feasibility-published-in-sustainability-journal",
    type: "Report",
    title: "HARC's Study on Floating Solar Feasibility Published in Sustainability Journal",
  },
  {
    slug: "texans-are-finding-common-ground-on-data-centers-data-centers-arent-cooperating",
    type: "Article",
    title: "Texans Are Finding Common Ground on Data Centers. Data Centers Aren't Cooperating.",
  },
  {
    slug: "harc-releases-virtual-reality-experience-of-living-lab-hq",
    type: "News",
    title: "HARC Releases Virtual Reality Experience of Living Lab HQ",
  },
];

const fmtDate = (d) => {
  if (!d) return "";
  const [y, m] = d.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `Updated ${months[Number(m) - 1]} ${y}`;
};

const INSIGHTS = INSIGHT_SLUGS.map(({ slug, type, title }) => {
  const match = siteUrls.find((u) => u.url.includes(`/news/${slug}/`));
  return {
    type,
    title,
    href: `https://harcresearch.org/news/${slug}/`,
    date: match ? fmtDate(match.lastmod) : "",
  };
});

export const Insights = () => (
  <section id="insights" className="mx-auto max-w-7xl px-6 py-28 md:py-36" data-testid="section-insights">
    <FadeUp>
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <SectionLabel>Insights & Resources</SectionLabel>
          <h2 className="harc-heading max-w-xl text-4xl font-normal leading-tight tracking-tight text-[#004778] md:text-5xl">
            The latest thinking from HARC's experts
          </h2>
        </div>
        <Link to="/insights" className="mockup-link-arrow" data-testid="cta-search-topic">
          Search for a specific Topic <ArrowRight size={15} aria-hidden="true" />
        </Link>
      </div>
    </FadeUp>
    <FadeUp delay={0.1} className="mt-14">
      <div role="list">
        {INSIGHTS.map((it) => (
          <a
            key={it.href}
            role="listitem"
            href={it.href}
            target="_blank"
            rel="noopener noreferrer"
            className="insight-row"
            data-testid={`insight-row-${it.type.toLowerCase()}`}
          >
            <TypeBadge type={it.type} />
            <span className="harc-heading text-lg leading-snug text-[#022A42] md:text-xl">
              {it.title}
            </span>
            <span className="text-xs uppercase tracking-[0.1em] text-[#5C6B72]">{it.date}</span>
            <ArrowRight size={18} className="row-arrow text-[#FFA100]" aria-hidden="true" />
          </a>
        ))}
        <div className="border-t border-[#004778]/20" aria-hidden="true" />
      </div>
    </FadeUp>
  </section>
);

export const FinalCta = () => {
  const submit = (e) => {
    e.preventDefault();
    alert("Mockup only: this will connect to HARC's existing Mailchimp list.");
  };
  return (
    <section id="conversation" className="bg-[#004778]" data-testid="section-final-cta">
      <div className="brand-stripe" aria-hidden="true" />
      <div className="mx-auto grid max-w-7xl gap-16 px-6 py-28 md:py-36">
        <FadeUp className="md:col-span-7">
          <h2 className="harc-heading text-4xl font-light leading-tight tracking-tight text-white md:text-6xl">
            Have a Complex Challenge? Let's Solve It Together.
          </h2>
          <p className="mt-6 max-w-lg text-lg font-light leading-relaxed text-white/85">
            Whether you're navigating an emerging issue, building a new initiative, or looking for
            the right partners and expertise, HARC can help turn insight into action.
          </p>
          <Link
            to="/start-a-conversation"
            data-testid="cta-talk-to-harc-final"
            className="mt-10 inline-flex items-center gap-3 rounded-full bg-[#FFA100] px-8 py-4 text-sm font-bold uppercase tracking-[0.12em] text-[#022A42] transition-colors hover:bg-[#D98A00]"
          >
            Talk to HARC <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </FadeUp>
        <FadeUp delay={0.15} className="md:col-span-4 md:col-start-9">
          <div className="border border-white/25 p-8">
            <h3 className="harc-heading text-2xl font-normal text-white">Stay Connected to HARC</h3>
            <p className="mt-3 text-sm leading-relaxed text-white/75">
              Get the latest insights, solutions, stories, and news delivered to your inbox.
            </p>
            <form onSubmit={submit} className="mt-6 flex flex-col gap-3" data-testid="newsletter-form">
              <label htmlFor="nl-email" className="sr-only">
                Email address
              </label>
              <input
                id="nl-email"
                type="email"
                required
                placeholder="Email address"
                data-testid="newsletter-email-input"
                className="w-full border border-white/40 bg-transparent px-4 py-3 text-sm text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-[#C9DA2D]"
              />
              <button
                type="submit"
                data-testid="newsletter-submit"
                className="inline-flex w-fit items-center gap-2 rounded-full border border-white px-6 py-2.5 text-xs font-bold uppercase tracking-[0.12em] text-white transition-colors hover:bg-white hover:text-[#004778]"
              >
                Sign Me Up <ArrowRight size={13} aria-hidden="true" />
              </button>
            </form>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};
